import { useRef, useState } from 'react';
import { Box, Link, Stack, Typography } from '@mui/material';

import { CasErrorDisplay, CasLoaderWithBackdrop } from 'src/compass/shared/components';
import useRetrieve from 'src/compass/shared/hooks/reactQuery/useRetrieve';

interface ILayoutInfoColumn {
  label: string;
  documentation: string;
}

interface ICasLayoutDefinitions {
  datasetLayoutId: string | number;
}

/**
 * The column definitions for one dataset layout: the emailed table's header row, then a definition
 * per documented column, with the header linking down to its definition.
 *
 * Lives in the application layer because two pages share it - the public Column Definitions page a
 * grower opens from an email, and the Message Dataset Columns preview an admin checks wording in.
 * They must not drift, or the preview stops being a preview.
 *
 * Fetches Public/LayoutInfo itself. On the public route axios sends the `uid` header; on an admin
 * route isPublicRoute() is false so it sends the Bearer token instead. The endpoint sits behind the
 * /api/public/ bypass and ignores both, so either caller works and neither goes near the
 * unknown-uid path.
 */
export default function CasLayoutDefinitions({ datasetLayoutId }: ICasLayoutDefinitions) {
  const [selected, setSelected] = useState<string | null>(null);
  const entryRefs = useRef<Record<string, HTMLLIElement | null>>({});

  const { data, isFetching, isError, error } = useRetrieve({
    url: 'Public/LayoutInfo',
    params: { datasetLayoutId },
    enabled: !!datasetLayoutId,
  });

  if (isFetching) return <CasLoaderWithBackdrop />;
  if (isError) return <CasErrorDisplay message={error?.message} />;
  if (!data) return null;

  const columns: ILayoutInfoColumn[] = data.columns ?? [];
  // The header row shows every column; only the documented ones earn an entry below it.
  const documented = columns.filter((col) => !!col.documentation);

  // block:'nearest' so a definition already on screen doesn't jump the page - the highlight is
  // enough on its own; scrolling only kicks in when the entry is actually out of view.
  const showDefinition = (label: string) => {
    setSelected(label);
    entryRefs.current[label]?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  };

  return (
    <>
      <Typography variant="h6" gutterBottom>
        Column definitions for the {data.layoutName} layout
      </Typography>

      {/* The emailed table's header row, reproduced so the reader can line the definitions up
          against the table they came from - same order, same styling as HTMLTableBuilder's
          thead. Scrolls on its own rather than widening the page: Prelim Settle is 17 columns. */}
      <Box sx={{ overflowX: 'auto', mb: 2 }}>
        <Box
          component="table"
          sx={{
            borderCollapse: 'collapse',
            fontSize: '0.875rem',
            '& th': {
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: 'grey.200',
              px: 1,
              py: 0.5,
              whiteSpace: 'nowrap',
              textAlign: 'left',
              fontWeight: 'fontWeightBold',
            },
          }}
        >
          <thead>
            <tr>
              {columns.map((col) =>
                col.documentation ? (
                  <th key={col.label}>
                    {/* A real button, not styled text: this is keyboard-reachable and the page is
                        sent to growers, who may well be on a phone or using assistive tech.
                        font:'inherit' keeps it looking like the bold header cell it sits in. */}
                    <Link
                      component="button"
                      type="button"
                      underline="hover"
                      onClick={() => showDefinition(col.label)}
                      sx={{ font: 'inherit', color: 'primary.main', cursor: 'pointer' }}
                    >
                      {col.label}
                    </Link>
                  </th>
                ) : (
                  // Undocumented columns stay plain - there is nothing below to jump to.
                  <th key={col.label}>{col.label}</th>
                )
              )}
            </tr>
          </thead>
        </Box>
      </Box>

      <Stack component="ul" spacing={1} sx={{ pl: 2.5, m: 0 }}>
        {documented.map((col) => (
          <Typography
            component="li"
            variant="body2"
            key={col.label}
            ref={(el: HTMLLIElement | null) => {
              entryRefs.current[col.label] = el;
            }}
            sx={{
              // Stays highlighted until another column is picked, so the reader can look back at
              // the table without losing their place.
              transition: (theme) => theme.transitions.create('background-color'),
              ...(selected === col.label && {
                bgcolor: 'warning.lighter',
                borderRadius: 0.5,
              }),
            }}
          >
            <b>{col.label}</b>:{' '}
            <Box component="span" sx={{ color: 'text.secondary' }}>
              {col.documentation}
            </Box>
          </Typography>
        ))}
      </Stack>
    </>
  );
}
